import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { login } from '../api/auth';
import { setToken } from '../auth';
import './LoginPage.css';

// Import logo
import luminaLogo from '../assets/luminalogo3.png';

const LoginPage = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });

    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            const { token } = await login(formData.email, formData.password);

            setToken(token);

            const from = location.state?.from?.pathname || "/";
            window.location.replace(from);
        } catch (err) {
            console.error("Login error:", err);

            const msg =
                (typeof err?.response?.data === "string" && err.response.data) ||
                err?.response?.data?.message ||
                err.message ||
                "Anmeldung fehlgeschlagen. Bitte prüfen Sie Ihre Zugangsdaten.";

            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-page">
            {/* Background particles */}
            <div className="login-particles">
                <div className="particles-glow" />
                {[...Array(20)].map((_, index) => (
                    <div key={index} className="particle" />
                ))}
            </div>

            {/* Main Container */}
            <motion.div
                className="login-container"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
            >
                {/* Logo */}
                <motion.div
                    className="login-logo"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1, delay: 0.2 }}
                >
                    <img src={luminaLogo} alt="Lumina Logo" />
                </motion.div>

                {/* Divider */}
                <motion.div
                    className="login-divider"
                    initial={{ width: 0 }}
                    animate={{ width: 80 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                />

                {/* Title */}
                <motion.h1
                    className="login-title"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                >
                    Willkommen zurück
                </motion.h1>

                <motion.p
                    className="login-subtitle"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                >
                    Melden Sie sich an, um fortzufahren
                </motion.p>

                {/* Error Message */}
                {error && (
                    <motion.div
                        className="error-message"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        style={{
                            color: '#ff6b6b',
                            backgroundColor: 'rgba(255, 107, 107, 0.1)',
                            padding: '12px 20px',
                            borderRadius: '8px',
                            marginBottom: '20px',
                            border: '1px solid rgba(255, 107, 107, 0.3)'
                        }}
                    >
                        {typeof error === 'string' ? error : 'Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.'}
                    </motion.div>
                )}

                {/* Login Form */}
                <motion.form
                    className="login-form"
                    onSubmit={handleSubmit}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.7 }}
                >
                    <div className="form-group">
                        <label htmlFor="email">E-Mail-Adresse</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            placeholder="ihre@email.de"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            disabled={loading}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">Passwort</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                name="password"
                                placeholder="Geben Sie Ihr Passwort ein"
                                value={formData.password}
                                onChange={handleInputChange}
                                required
                                disabled={loading}
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                                disabled={loading}
                            >
                                {showPassword ? '👁️' : '👁️‍🗨️'}
                            </button>
                        </div>
                    </div>


                    <motion.button
                        type="submit"
                        className="login-submit-btn"
                        whileHover={!loading ? { scale: 1.02, y: -2 } : {}}
                        whileTap={!loading ? { scale: 0.98 } : {}}
                        disabled={loading}
                    >
                        {loading ? 'Anmeldung läuft…' : 'Anmelden'}
                    </motion.button>
                </motion.form>

                {/* Register Link */}
                <motion.div
                    className="register-link"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.9 }}
                >
                    <p>
                        Noch kein Konto?{' '}
                        <button onClick={() => navigate('/register')} disabled={loading}>
                            Konto erstellen
                        </button>
                    </p>
                </motion.div>

                {/* Back to Home */}
                <motion.button
                    className="back-home-link"
                    onClick={() => navigate('/')}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 1 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    disabled={loading}
                >
                    ← Zur Startseite
                </motion.button>
            </motion.div>
        </div>
    );
};

export default LoginPage;
